//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

interface Props extends React.HTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  scope: 'col' | 'row' | 'rowgroup' | 'colgroup';
}

/**
 * This component exists for the purpose of standardizing table header cells `<th>`
 *
 * @param children A component rendering the contents of a table header cell.
 * @param {'row' | 'col' | 'rowgroup' | 'colgroup' } scope Defines the set of cells the header relates to
 * TableHeaderCellText should be used for properly styled text.
 * Note that TableHeaderCellText has a default justifyContent of 'flex-start'.
 *
 * @example
 * <TableHeaderCell scope='col'>
 *   <TableHeaderCellText>Column Name</TableHeaderCellText>
 * </TableHeaderCell>
 */
export const TableHeaderCell: React.FunctionComponent<Props> = ({
  children,
  className,
  scope,
  ...cellProps
}) => {
  return (
    <StyledTH scope={scope} className={className} {...cellProps}>
      {children}
    </StyledTH>
  );
};

const StyledTH = styled.th(
  ({ theme }) => `
    padding: ${theme.spacings[3]} ${theme.spacings[1]};
    
    &:first-child {
      padding: ${theme.spacings[3]} ${theme.spacings[1]} ${theme.spacings[3]} ${theme.spacings[3]};
    }
    &:last-child {
      padding: ${theme.spacings[3]} ${theme.spacings[4]} ${theme.spacings[3]} ${theme.spacings[1]};      
    }
  `,
);
